import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Row,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileSearchOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { getHeaders } from '../../utility/getHeader';

const { Text } = Typography;

const STATUS_TAG = {
  verified: { color: 'success',    icon: <CheckCircleOutlined />,  label: 'VERIFIED' },
  pending:  { color: 'processing', icon: <ClockCircleOutlined />,  label: 'PENDING'  },
};

const RESULT_TAG = {
  verified:  { color: 'success' },
  duplicate: { color: 'warning' },
  not_found: { color: 'error' },
  error:     { color: 'error' },
};

export default function GateTicketDetails() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const inputRef = useRef(null);

  const uname    = localStorage.getItem('uname')    || sessionStorage.getItem('uname')    || 'Gate User';
  const usertype = localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '';

  const canSearch = useMemo(() => query.trim().length > 0 && !loading, [query, loading]);

  const loadDetails = async () => {
    const value = query.trim();
    if (!value) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `/gate/ticket-details/${encodeURIComponent(value)}`,
        getHeaders()
      );
      const data = response.data?.data || null;
      setDetails(data);
      if (data) {
        toast.success('Ticket details loaded');
        setQuery('');
      } else {
        toast.info('No details found for this barcode');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ticket not found');
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const onKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); loadDetails(); } };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  const statusTag = details ? (STATUS_TAG[details.verify_status] || { color: 'default', label: details.verify_status }) : null;

  const descItems = details ? [
    { key: 'barcode',   label: 'Barcode',       children: <Text copyable>{details.barcode_value}</Text> },
    { key: 'ticket_id', label: 'Ticket ID',     children: details.ticket_id     || '-' },
    { key: 'bill',      label: 'Bill Number',   children: details.bill_number   || '-' },
    { key: 'mode',      label: 'Print Mode',    children: details.print_mode    || '-' },
    { key: 'date',      label: 'Setup Date',    children: details.setup_date    || '-' },
    { key: 'cashier',   label: 'Cashier',       children: details.cashier_name  || details.cashier_user_name || details.cashier_uname || '-' },
    { key: 'payment',   label: 'Payment',       children: details.payment_method || '-' },
    { key: 'total',     label: 'Total Amount',  children: details.total_amount  || '-' },
    { key: 'customer',  label: 'Customer',      children: details.customer_name || '-' },
    { key: 'phone',     label: 'Phone',         children: details.customer_phone || '-' },
    { key: 'verifby',   label: 'Verified By',   children: details.verified_by   || '-' },
    { key: 'verifat',   label: 'Verified At',   children: details.verified_at   || '-' },
    { key: 'vcount',    label: 'Verify Count',  children: details.verify_count ?? 0 },
    { key: 'created',   label: 'Created At',    children: details.created_at    || '-' },
  ] : [];

  const itemColumns = [
    { title: 'Ticket ID',  dataIndex: 'ticket_id',     key: 'ticket_id',     render: v => v || '-' },
    { title: 'Item',       dataIndex: 'item_name',     key: 'item_name' },
    { title: 'Category',   dataIndex: 'category_name', key: 'category_name', render: v => v || '-' },
    { title: 'Qty',        dataIndex: 'quantity',      key: 'quantity',      width: 55 },
    { title: 'Unit',       dataIndex: 'unit_price',    key: 'unit_price',    width: 80 },
    { title: 'Total',      dataIndex: 'total_price',   key: 'total_price',   width: 80 },
  ];

  const logColumns = [
    { title: 'Time',      dataIndex: 'scanned_at',      key: 'scanned_at',      render: v => <Text style={{ fontSize: 11 }}>{v}</Text>, width: 130 },
    { title: 'Gate User', dataIndex: 'gate_user_uname', key: 'gate_user_uname', render: v => v || '-' },
    { title: 'Role',      dataIndex: 'gate_user_type',  key: 'gate_user_type',  render: v => v || '-', width: 100 },
    {
      title: 'Result', dataIndex: 'scan_result', key: 'scan_result', width: 100,
      render: v => <Tag color={RESULT_TAG[v]?.color || 'default'}>{(v || '').toUpperCase()}</Tag>,
    },
    { title: 'Message', dataIndex: 'scan_message', key: 'scan_message', render: v => v || '-' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile Header Bar */}
      <div style={{
        background: '#1d4ed8',
        color: '#fff',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link to="/gate/scan" style={{ color: '#bfdbfe', display: 'flex', alignItems: 'center' }}>
            <ArrowLeftOutlined style={{ fontSize: 18 }} />
          </Link>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
              <FileSearchOutlined style={{ marginRight: 6 }} />Ticket Details
            </div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>{uname} · {usertype}</div>
          </div>
        </div>
        <Button
          type="text"
          size="small"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          style={{ color: '#fca5a5' }}
        >
          Logout
        </Button>
      </div>

      {/* Scrollable Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>

        {/* Search Card */}
        <Card
          bordered={false}
          style={{ borderRadius: 14, marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}
          bodyStyle={{ padding: '16px 14px' }}
        >
          <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>
            <FileSearchOutlined style={{ marginRight: 6, color: '#1d4ed8' }} />
            Check Ticket Details
          </Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            Enter barcode value or ticket ID to view full details and scan history.
          </Text>
          <Input.Search
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            onSearch={loadDetails}
            placeholder="Barcode value or ticket ID"
            enterButton={
              <Button
                type="primary"
                loading={loading}
                disabled={!canSearch}
                style={{ background: '#1d4ed8', borderColor: '#1d4ed8', minWidth: 110 }}
              >
                {loading ? 'Searching…' : 'Check Details'}
              </Button>
            }
            size="large"
            style={{ fontSize: 16 }}
            disabled={loading}
          />
        </Card>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <Spin size="large" tip="Loading ticket details…" />
          </div>
        )}

        {/* Results */}
        {!loading && details && (
          <>
            {/* Ticket Info */}
            <Card
              bordered={false}
              style={{ borderRadius: 14, marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}
              bodyStyle={{ padding: '14px 14px' }}
            >
              <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
                <Col>
                  <Text strong style={{ fontSize: 15 }}>Ticket Snapshot</Text>
                </Col>
                <Col>
                  {statusTag && (
                    <Tag color={statusTag.color} icon={statusTag.icon} style={{ fontSize: 13, padding: '4px 10px' }}>
                      {statusTag.label}
                    </Tag>
                  )}
                </Col>
              </Row>
              <Descriptions
                items={descItems}
                column={1}
                size="small"
                bordered
                labelStyle={{ fontWeight: 600, background: '#f8fafc', width: '38%' }}
                contentStyle={{ wordBreak: 'break-word' }}
              />
            </Card>

            {/* Bill Items */}
            {details.items?.length > 0 && (
              <Card
                title={<Text strong>Bill Items</Text>}
                bordered={false}
                style={{ borderRadius: 14, marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}
                bodyStyle={{ padding: '0 0 8px 0' }}
              >
                <Table
                  columns={itemColumns}
                  dataSource={(details.items || []).map((r, i) => ({ ...r, key: `${r.ticket_id || 'x'}-${i}` }))}
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </Card>
            )}

            {/* Scan History */}
            <Card
              title={<Text strong>Scan History</Text>}
              bordered={false}
              style={{ borderRadius: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}
              bodyStyle={{ padding: '0 0 8px 0' }}
            >
              {details.scan_logs?.length > 0 ? (
                <Table
                  columns={logColumns}
                  dataSource={(details.scan_logs || []).map(r => ({ ...r, key: r.id }))}
                  pagination={{ pageSize: 10, size: 'small' }}
                  size="small"
                  scroll={{ x: true }}
                />
              ) : (
                <div style={{ padding: '16px 14px', color: '#9ca3af' }}>No scan history for this ticket.</div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
